import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { leaveSlotService, LeaveSlot } from '../../services/availability/leaveSlotService';
import { profileService, DoctorPractice } from '../../services/profile/profileService';
import { authService } from '../../services/auth/authService';
import AvailabilityCalendar from '../../components/availability/AvailabilityCalendar/AvailabilityCalendar';
import AddLeaveForm from '../../components/availability/AddLeaveForm/AddLeaveForm';
import LeaveDetailsView from '../../components/availability/LeaveDetailsView/LeaveDetailsView';
import EditLeaveForm from '../../components/availability/EditLeaveForm/EditLeaveForm';
import { UserMenu } from '../../components/layout/UserMenu/UserMenu';
import './EditAvailabilityPage.css';

type LeaveStatus = 'active' | 'cancelled' | 'expired' | 'pending_confirmation' | null;

const EditAvailabilityPage: React.FC = () => {
  const navigate = useNavigate();
  const [leaveSlots, setLeaveSlots] = useState<LeaveSlot[]>([]);
  const [practices, setPractices] = useState<DoctorPractice[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [displayedMonth, setDisplayedMonth] = useState<Date>(new Date());
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<LeaveStatus>(null);
  const [appointmentCounts, setAppointmentCounts] = useState<Record<string, number>>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingAppointmentCounts, setIsLoadingAppointmentCounts] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [error, setError] = useState<string | null>(null);
  const [showingAddLeave, setShowingAddLeave] = useState<boolean>(false);
  const [selectedLeaveSlot, setSelectedLeaveSlot] = useState<LeaveSlot | null>(null);
  const [editingLeaveSlot, setEditingLeaveSlot] = useState<LeaveSlot | null>(null);
  const [deletingLeaveSlotId, setDeletingLeaveSlotId] = useState<number | null>(null);
  const [showingDeleteConfirm, setShowingDeleteConfirm] = useState<boolean>(false);

  const formatDateString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const loadLeaveSlots = useCallback(async () => {
    try {
      const today = new Date();
      const endDate = new Date();
      endDate.setDate(today.getDate() + 90);

      const startDateStr = formatDateString(today);
      const endDateStr = formatDateString(endDate);
      const statusStr = selectedStatusFilter || undefined;

      const slots = await leaveSlotService.getLeaveSlots({
        start_date: startDateStr,
        end_date: endDateStr,
        status: statusStr,
      });

      setLeaveSlots(slots);
    } catch (err: any) {
      setError(err.message || 'Failed to load leave slots');
    }
  }, [selectedStatusFilter]);

  const loadAppointmentCountsForMonth = useCallback(async (month: Date) => {
    try {
      setIsLoadingAppointmentCounts(true);
      const calendar = new Date(month.getFullYear(), month.getMonth(), 1);
      const monthStart = new Date(calendar);
      const monthEnd = new Date(calendar.getFullYear(), calendar.getMonth() + 1, 0);

      const startDateStr = formatDateString(monthStart);
      const endDateStr = formatDateString(monthEnd);

      const counts = await leaveSlotService.getAppointmentCounts(startDateStr, endDateStr);
      setAppointmentCounts((prev) => ({ ...prev, ...counts }));
    } catch (err: any) {
      console.error('Failed to load appointment counts:', err);
    } finally {
      setIsLoadingAppointmentCounts(false);
    }
  }, []);

  const loadInitialData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [practicesData] = await Promise.allSettled([
        profileService.getPractices(),
      ]);

      if (practicesData.status === 'fulfilled') {
        setPractices(practicesData.value);
      }

      await loadLeaveSlots();
      await loadAppointmentCountsForMonth(displayedMonth);
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }, [displayedMonth, loadLeaveSlots, loadAppointmentCountsForMonth]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    loadLeaveSlots();
  }, [loadLeaveSlots]);

  useEffect(() => {
    loadAppointmentCountsForMonth(displayedMonth);
  }, [displayedMonth, loadAppointmentCountsForMonth]);

  const leaveSlotsForDate = (date: Date): LeaveSlot[] => {
    const dateStr = formatDateString(date);
    return leaveSlots.filter((slot) => slot.leave_date === dateStr);
  };

  const hasFullDayLeave = (date: Date): boolean => {
    const slots = leaveSlotsForDate(date).filter(
      (slot) => slot.status.toLowerCase() !== 'cancelled'
    );
    return (
      slots.some((slot) => slot.start_time === '00:00' && slot.end_time === '23:59') ||
      slots.reduce((total, slot) => {
        const duration = durationInHours(slot.start_time, slot.end_time);
        return total + duration;
      }, 0) >= 8.0
    );
  };

  const hasPartialLeave = (date: Date): boolean => {
    const slots = leaveSlotsForDate(date).filter(
      (slot) => slot.status.toLowerCase() !== 'cancelled'
    );
    return slots.length > 0 && !hasFullDayLeave(date);
  };

  const durationInHours = (start: string, end: string): number => {
    const [startHour, startMin] = start.split(':').map(Number);
    const [endHour, endMin] = end.split(':').map(Number);
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    return (endMinutes - startMinutes) / 60;
  };

  const isDateEnabled = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const normalizedDate = new Date(date);
    normalizedDate.setHours(0, 0, 0, 0);

    if (normalizedDate < today) {
      return false;
    }

    // If no practices, disable all dates
    if (practices.length === 0) {
      return false;
    }

    // For now, enable all future dates if practices exist
    // In the future, we can check practice timings to determine enabled dates
    return true;
  };

  const handleMonthChange = (month: Date) => {
    setDisplayedMonth(month);
    // Load leave slots for the new month
    loadLeaveSlotsForMonth(month);
  };

  const loadLeaveSlotsForMonth = async (month: Date) => {
    try {
      const calendar = new Date(month.getFullYear(), month.getMonth(), 1);
      const monthStart = new Date(calendar);
      const monthEnd = new Date(calendar.getFullYear(), calendar.getMonth() + 1, 0);

      const startDateStr = formatDateString(monthStart);
      const endDateStr = formatDateString(monthEnd);
      const statusStr = selectedStatusFilter || undefined;

      const slots = await leaveSlotService.getLeaveSlots({
        start_date: startDateStr,
        end_date: endDateStr,
        status: statusStr,
      });

      // Merge with existing slots (remove old ones for this month and add new ones)
      const monthStr = `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, '0')}`;
      setLeaveSlots((prev) => {
        const filtered = prev.filter((slot) => {
          const slotDate = new Date(slot.leave_date);
          const slotMonthStr = `${slotDate.getFullYear()}-${String(slotDate.getMonth() + 1).padStart(2, '0')}`;
          return slotMonthStr !== monthStr;
        });
        return [...filtered, ...slots];
      });
    } catch (err: any) {
      console.error('Failed to load leave slots for month:', err);
    }
  };

  const slotsForSelectedDate = leaveSlotsForDate(selectedDate);

  const formatTime = (time: string): string => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatLeaveType = (type: string): string => {
    const typeMap: Record<string, string> = {
      holiday: 'Holiday',
      sick_leave: 'Sick Leave',
      emergency: 'Emergency',
      other: 'Other',
    };
    return typeMap[type] || type;
  };

  const formatLeaveScope = (scope: string): string => {
    return scope === 'all_practices' ? 'All Practices' : 'Single Practice';
  };

  const handleEditLeave = (slot: LeaveSlot, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingLeaveSlot(slot);
  };

  const handleDeleteLeave = (slot: LeaveSlot, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeletingLeaveSlotId(slot.id);
    setShowingDeleteConfirm(true);
  };

  const confirmDeleteLeave = async () => {
    if (!deletingLeaveSlotId) return;

    try {
      await leaveSlotService.cancelLeaveSlot(deletingLeaveSlotId, true);
      setShowingDeleteConfirm(false);
      setDeletingLeaveSlotId(null);
      loadLeaveSlots();
      loadAppointmentCountsForMonth(displayedMonth);
    } catch (err: any) {
      console.error('Failed to delete leave:', err);
      alert(err.response?.data?.detail || err.message || 'Failed to delete leave');
    }
  };

  const handleGoToProfile = () => {
    navigate('/profile');
  };

  const handleLogout = () => {
    authService.clearTokens();
    navigate('/login');
  };

  if (isLoading) {
    return (
      <div className="edit-availability-page">
        <div className="edit-availability-loading">Loading availability...</div>
      </div>
    );
  }

  return (
    <div className="edit-availability-page">
      <div className="edit-availability-header">
        <div className="app-logo" onClick={() => navigate('/dashboard')}>
          <img src="/images/zivo_logo.png" alt="Zivo" />
        </div>
        <UserMenu onProfileClick={handleGoToProfile} onLogout={handleLogout} />
      </div>

      <div className="page-pane">
        <h1 className="page-title">Edit Availability</h1>

        <div className="edit-availability-content">
          {/* Status Filter Chips */}
          <div className="status-filter-chips">
            <button
              type="button"
              className={`status-filter-chip ${selectedStatusFilter === null ? 'active' : ''}`}
              onClick={() => setSelectedStatusFilter(null)}
            >
              All
            </button>
            <button
              type="button"
              className={`status-filter-chip ${selectedStatusFilter === 'active' ? 'active' : ''}`}
              onClick={() => setSelectedStatusFilter('active')}
            >
              Active
            </button>
            <button
              type="button"
              className={`status-filter-chip ${selectedStatusFilter === 'pending_confirmation' ? 'active' : ''}`}
              onClick={() => setSelectedStatusFilter('pending_confirmation')}
            >
              Pending
            </button>
            <button
              type="button"
              className={`status-filter-chip ${selectedStatusFilter === 'cancelled' ? 'active' : ''}`}
              onClick={() => setSelectedStatusFilter('cancelled')}
            >
              Cancelled
            </button>
          </div>

          {/* Calendar */}
          <AvailabilityCalendar
            selectedDate={selectedDate}
            displayedMonth={displayedMonth}
            leaveSlots={leaveSlots}
            appointmentCounts={appointmentCounts}
            onDateSelect={setSelectedDate}
            onMonthChange={handleMonthChange}
            hasFullDayLeave={hasFullDayLeave}
            hasPartialLeave={hasPartialLeave}
            isDateEnabled={isDateEnabled}
            isLoadingAppointmentCounts={isLoadingAppointmentCounts}
          />

          {/* Add Leave Button */}
          <div className="add-leave-button-container">
            <button
              type="button"
              className="add-leave-button"
              onClick={() => setShowingAddLeave(true)}
            >
              <span className="add-leave-icon">+</span>
              <span>Add Leave</span>
            </button>
          </div>

          {/* Scheduled Leave List */}
          <div className="scheduled-leave-section">
            <h2 className="scheduled-leave-title">
              Scheduled Leave for {selectedDate.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </h2>
            {slotsForSelectedDate.length === 0 ? (
              <div className="no-leave-message">No leave scheduled for this date.</div>
            ) : (
              <div className="leave-slots-list">
                {slotsForSelectedDate.map((slot) => (
                  <div
                    key={slot.id}
                    className="leave-slot-card leave-slot-card-clickable"
                    onClick={() => setSelectedLeaveSlot(slot)}
                  >
                    <div className="leave-slot-header">
                      <div className="leave-slot-time">
                        {formatTime(slot.start_time)} - {formatTime(slot.end_time)}
                      </div>
                      <div className="leave-slot-header-right">
                        <div className={`leave-slot-status leave-slot-status-${slot.status}`}>
                          {slot.status}
                        </div>
                        <div className="leave-slot-actions">
                          <button
                            type="button"
                            className="leave-slot-action-button leave-slot-edit-button"
                            onClick={(e) => handleEditLeave(slot, e)}
                            title="Edit leave"
                          >
                            ✏️
                          </button>
                          <button
                            type="button"
                            className="leave-slot-action-button leave-slot-delete-button"
                            onClick={(e) => handleDeleteLeave(slot, e)}
                            title="Delete leave"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="leave-slot-details">
                      <div className="leave-slot-detail-row">
                        <span className="leave-slot-label">Type:</span>
                        <span className="leave-slot-value">{formatLeaveType(slot.leave_type)}</span>
                      </div>
                      {slot.reason && (
                        <div className="leave-slot-detail-row">
                          <span className="leave-slot-label">Reason:</span>
                          <span className="leave-slot-value">{slot.reason}</span>
                        </div>
                      )}
                      <div className="leave-slot-detail-row">
                        <span className="leave-slot-label">Scope:</span>
                        <span className="leave-slot-value">{formatLeaveScope(slot.leave_scope)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {showingAddLeave && (
        <div className="modal-overlay" onClick={() => setShowingAddLeave(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <AddLeaveForm
              selectedDate={selectedDate}
              practices={practices}
              onClose={() => setShowingAddLeave(false)}
              onSuccess={() => {
                loadLeaveSlots();
                loadAppointmentCountsForMonth(displayedMonth);
              }}
            />
          </div>
        </div>
      )}

      {selectedLeaveSlot && (
        <div className="modal-overlay" onClick={() => setSelectedLeaveSlot(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <LeaveDetailsView
              leave={selectedLeaveSlot}
              practices={practices}
              onClose={() => setSelectedLeaveSlot(null)}
              onUpdate={() => {
                loadLeaveSlots();
                loadAppointmentCountsForMonth(displayedMonth);
              }}
              onDelete={() => {
                loadLeaveSlots();
                loadAppointmentCountsForMonth(displayedMonth);
              }}
            />
          </div>
        </div>
      )}

      {editingLeaveSlot && (
        <div className="modal-overlay" onClick={() => setEditingLeaveSlot(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <EditLeaveForm
              leave={editingLeaveSlot}
              practices={practices}
              onClose={() => setEditingLeaveSlot(null)}
              onSuccess={() => {
                setEditingLeaveSlot(null);
                loadLeaveSlots();
                loadAppointmentCountsForMonth(displayedMonth);
              }}
            />
          </div>
        </div>
      )}

      {showingDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowingDeleteConfirm(false)}>
          <div className="modal-content-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Leave</h3>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this leave? This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="modal-button-secondary"
                onClick={() => {
                  setShowingDeleteConfirm(false);
                  setDeletingLeaveSlotId(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="modal-button-danger"
                onClick={confirmDeleteLeave}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditAvailabilityPage;

